import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  SUPPORT_TICKET_ACKNOWLEDGEMENT,
  SUPPORT_TICKET_ACKNOWLEDGEMENT_VERSION,
  SUPPORT_TICKET_CATEGORIES,
  SUPPORT_TICKET_CUI_WARNING,
  SUPPORT_TICKET_FIELD_LABELS,
  SUPPORT_TICKET_FIELD_LIMITS,
  SUPPORT_TICKET_FIELD_PROVENANCE_LABELS,
  SUPPORT_TICKET_MAX_ATTACHMENTS,
  SUPPORT_TICKET_PROTECTED_INFO_INDICATOR,
  SUPPORT_TICKET_REQUIRED_FIELDS,
  validateSupportTicketFields,
} from '@dha-nexus/shared';

import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { PageHeader } from '../../components/primitives.jsx';
import { listFeedbackPackages } from '../feedbackMarkup/feedbackApi.js';
import { scanClientSensitiveContent } from './clientSensitiveWarnings.js';
import { collectSupportContext } from './collectSupportContext.js';
import { useIntakeDraftAutosave } from './useIntakeDraftAutosave.js';
import {
  createSupportTicketIntake,
  acknowledgeSupportTicketCui,
  gscTicketEmlUrl,
  getSupportTicketDelivery,
  getSupportTicketIntake,
  newIdempotencyKey,
  patchSupportTicketIntake,
  prepareSupportTicketIntake,
  submitSupportTicketIntake,
} from './supportTicketApi.js';
import './feedbackIntake.css';

// Fields the form edits, in render order. `title` is a PRIME-side short summary
// (the governed record's title and what a reviewer sees in the queue); the GSC
// form has no equivalent, so it is labelled as ours rather than as a desk field.
const TEXT_FIELDS = Object.freeze([
  'name',
  'phone',
  'email',
  'site',
  'organization',
  'department',
  'buildingNumber',
  'roomNumber',
  'fullComputerName',
]);

const BASE_STEPS = Object.freeze([
  { id: 'describe', label: 'Describe the problem' },
  { id: 'review', label: 'Review delivery' },
  { id: 'sent', label: 'Confirmation' },
]);

const EMPTY_FIELDS = Object.freeze({
  title: '',
  description: '',
  name: '',
  phone: '',
  email: '',
  site: '',
  organization: '',
  department: '',
  buildingNumber: '',
  roomNumber: '',
  fullComputerName: '',
  category: '',
  protectedInformationRequired: false,
  cuiAcknowledged: false,
});

function fieldId(field) {
  return `support-ticket-${field}`;
}

// Reporter-facing label for a validation key. The server reports the description
// under its persisted key (`details.description`); the form knows which control
// that is.
function validationLabel(key) {
  if (key === 'details.description') return SUPPORT_TICKET_FIELD_LABELS.description;
  if (key === 'title') return 'Short summary';
  if (key === 'cuiAcknowledged') return 'Protected-information confirmation';
  return SUPPORT_TICKET_FIELD_LABELS[key] || key;
}

function validationControlId(key) {
  if (key === 'details.description') return fieldId('description');
  return fieldId(key);
}

// Prefill provenance. `directory_supplied` means "PRIME knows this about you";
// anything the reporter types becomes `user_entered` the moment they touch it, so
// the label can never claim a directory source for an edited value.
function initialProvenance(user) {
  const provenance = {};
  if (user?.displayName) provenance.name = 'directory_supplied';
  if (user?.email) provenance.email = 'directory_supplied';
  if (user?.homeOrganizationId) provenance.organization = 'directory_supplied';
  // A browser cannot discover the machine name, so it is always user-entered.
  provenance.fullComputerName = 'user_entered';
  return provenance;
}

function fieldsFromIntake(intake) {
  const supportTicket = intake?.supportTicket || {};
  return {
    ...EMPTY_FIELDS,
    title: intake?.title || '',
    description: intake?.details?.description || '',
    ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, supportTicket[field] || ''])),
    category: supportTicket.category || '',
    protectedInformationRequired: supportTicket.protectedInformationRequired === true,
    cuiAcknowledged: supportTicket.cuiAcknowledged === true,
  };
}

function CuiWarning({ id, deliveryMode = null }) {
  const body =
    deliveryMode === 'draft_only'
      ? 'PRIME will record this ticket and prepare an email draft. PRIME will not email the service desk automatically. Do not include CUI, credentials, patient information, or other protected information.'
      : deliveryMode === 'email'
        ? SUPPORT_TICKET_CUI_WARNING.body
        : 'PRIME prepares service-desk email content from this ticket. Do not include CUI, credentials, patient information, or other protected information.';
  const followUpNote =
    deliveryMode === 'draft_only'
      ? 'After PRIME records the ticket, download the .eml file and send it from your approved mail client.'
      : SUPPORT_TICKET_CUI_WARNING.followUpNote;
  return (
    <section className="support-ticket-cui" aria-labelledby={`${id}-heading`} data-testid={id}>
      <h3 id={`${id}-heading`}>{SUPPORT_TICKET_CUI_WARNING.heading}</h3>
      <p>{body}</p>
      <div className="support-ticket-cui-examples">
        <div className="support-ticket-cui-example">
          <b>{SUPPORT_TICKET_CUI_WARNING.goodExampleLabel}</b>
          {SUPPORT_TICKET_CUI_WARNING.goodExample}
        </div>
        <div className="support-ticket-cui-example">
          <b>{SUPPORT_TICKET_CUI_WARNING.badExampleLabel}</b>
          {SUPPORT_TICKET_CUI_WARNING.badExample}
        </div>
      </div>
      <p>{followUpNote}</p>
    </section>
  );
}

function FindingList({ findings, testId, forceBlocking = false, recordOnly = false }) {
  if (!findings.length) return null;
  const blocking = forceBlocking || findings.some((finding) => finding.severity === 'block');
  return (
    <div
      className={`support-ticket-alert ${blocking ? 'tone-danger' : 'tone-warn'}`}
      role="alert"
      data-testid={testId}
    >
      <h3>
        {blocking
          ? recordOnly
            ? 'Remove the protected information before recording'
            : 'Remove the protected information before sending'
          : 'This may contain protected information'}
      </h3>
      <ul>
        {findings.map((finding) => (
          <li key={`${finding.detector}-${finding.field}`}>
            <b>{validationLabel(finding.field)}:</b> {finding.message}{' '}
            <span className="support-ticket-finding-evidence">{finding.redactedEvidence}</span>
            {finding.source === 'client_preview' ? ' (checked in your browser)' : null}
          </li>
        ))}
      </ul>
      <p className="support-ticket-card-hint">
        PRIME never shows the matched text back to you — only the shape it found.
      </p>
    </div>
  );
}

function DeliveryState({ delivery, testId }) {
  if (!delivery) return null;
  const glyph = delivery.deliveredExternally ? '✓' : 'i';
  return (
    <div className="support-ticket-delivery" data-testid={testId}>
      <span className="support-ticket-delivery-state" data-glyph={glyph}>
        {delivery.statusLabel}
      </span>
      <span className="support-ticket-card-hint">{delivery.statusDetail}</span>
      {delivery.recipients?.to?.length ? (
        <span className="support-ticket-card-hint">
          Addressed to {delivery.recipients.to.join(', ')}
          {delivery.recipients.cc?.length ? ` (cc ${delivery.recipients.cc.join(', ')})` : ''} —
          PRIME sets these recipients.
        </span>
      ) : null}
      {delivery.recipients?.recipientPosture === 'user_addressed_draft' ? (
        <>
          <span className="support-ticket-card-hint">
            Add recipients in your approved mail client.
          </span>
          <span className="support-ticket-card-hint">
            PRIME did not select recipients, send this message, or verify delivery.
          </span>
        </>
      ) : null}
    </div>
  );
}

const DELIVERY_UNAVAILABLE_PROJECTION = Object.freeze({
  present: false,
  status: 'unavailable',
  statusLabel: 'Delivery status unavailable right now',
  statusDetail: 'PRIME could not load the stored delivery status. Try again later.',
  deliveredExternally: false,
  recipients: null,
});

export function SupportTicketPage() {
  const { intakeId: routeIntakeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, roles } = useCurrentUser();

  const [step, setStep] = useState('describe');
  const [intake, setIntake] = useState(null);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [provenance, setProvenance] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [availableCaptures, setAvailableCaptures] = useState(null);
  const [captureLoadError, setCaptureLoadError] = useState(null);
  const [loadState, setLoadState] = useState(routeIntakeId ? 'loading' : 'ready');
  const [loadError, setLoadError] = useState(null);
  const [validationKeys, setValidationKeys] = useState([]);
  const [serverFindings, setServerFindings] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [busy, setBusy] = useState(false);

  // One idempotency key per logical create and per logical submit, held for the
  // life of the attempt: a retry after an ambiguous failure must reuse it so the
  // server replays the original result rather than creating a second intake or a
  // second governed record.
  const createKeyRef = useRef(newIdempotencyKey());
  const acknowledgementKeyRef = useRef(newIdempotencyKey());
  const submitKeyRef = useRef(newIdempotencyKey());
  const intakeRef = useRef(null);
  const validationSummaryRef = useRef(null);

  intakeRef.current = intake;

  // The context envelope is a PROPOSAL rebuilt from the reporter's session. The
  // route it describes is the page they came FROM, not this form.
  const proposedContext = useMemo(
    () =>
      collectSupportContext({
        pathname: location.state?.fromRoute || '/',
        pageTitle: location.state?.fromTitle || null,
      }),
    [location.state?.fromRoute, location.state?.fromTitle],
  );

  // Prefill from the session, once, and only into empty fields.
  useEffect(() => {
    if (routeIntakeId || !user) return;
    setFields((current) => ({
      ...current,
      name: current.name || user.displayName || '',
      email: current.email || user.email || '',
      organization: current.organization || user.homeOrganizationId || '',
    }));
    setProvenance((current) => ({ ...initialProvenance(user), ...current }));
  }, [routeIntakeId, user]);

  // Resume a draft.
  //
  // Guarded on `intakeRef.current` because the FIRST save replaces the URL with
  // the draft's own route, which changes `routeIntakeId` from undefined to the new
  // id. Without the guard that navigation would re-fetch the aggregate and
  // overwrite whatever the reporter typed in the meantime with the server copy —
  // losing keystrokes made during the save round trip.
  useEffect(() => {
    if (!routeIntakeId || intakeRef.current) return;
    let cancelled = false;
    (async () => {
      let loaded;
      try {
        const response = await getSupportTicketIntake(routeIntakeId);
        if (cancelled) return;
        loaded = response?.data || null;
        setIntake(loaded);
        setFields(fieldsFromIntake(loaded));
        setProvenance(loaded?.supportTicket?.fieldProvenance || {});
        setAttachments(loaded?.supportTicket?.attachments || []);
      } catch (error) {
        if (cancelled) return;
        setLoadError(error);
        setLoadState('error');
        return;
      }
      if (loaded?.status === 'submitted') {
        let dispatch = DELIVERY_UNAVAILABLE_PROJECTION;
        try {
          const deliveryResponse = await getSupportTicketDelivery(routeIntakeId);
          if (cancelled) return;
          dispatch = deliveryResponse?.data || null;
        } catch {
          // The durable ticket remains usable when its secondary delivery
          // projection is temporarily unavailable.
        }
        if (cancelled) return;
        setStep('sent');
        setSubmission({ intake: loaded, dispatch });
      }
      setLoadState('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [routeIntakeId]);

  const clientFindings = useMemo(
    () =>
      scanClientSensitiveContent({
        title: fields.title,
        'details.description': fields.description,
        ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, fields[field]])),
      }),
    [fields],
  );

  const structuralValidation = useMemo(
    () =>
      validateSupportTicketFields({
        ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, fields[field]])),
        category: fields.category,
        description: fields.description,
        // The acknowledgement lives on the review step, so the describe step is
        // validated as if it were already given; the server still enforces it.
        cuiAcknowledged: true,
      }),
    [fields],
  );

  const buildPayload = useCallback(
    () => ({
      title: fields.title.trim() || null,
      sourceRoute: proposedContext.routeTemplate || null,
      details: { description: fields.description },
      supportTicket: {
        ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, fields[field]])),
        category: fields.category,
        protectedInformationRequired: fields.protectedInformationRequired,
        fieldProvenance: provenance,
        attachments: attachments.map(({ packageId, captureId }) => ({ packageId, captureId })),
      },
      supportContext: proposedContext,
    }),
    [attachments, fields, proposedContext, provenance],
  );

  const persist = useCallback(
    async (payload) => {
      const current = intakeRef.current;
      if (!current) {
        const response = await createSupportTicketIntake(payload, {
          idempotencyKey: createKeyRef.current,
        });
        const created = response?.data;
        setIntake(created);
        intakeRef.current = created;
        // Give the draft a durable URL so a refresh or a closed tab resumes it.
        navigate(`/feedback/support/${created.id}`, {
          replace: true,
          state: location.state || null,
        });
        return created;
      }
      const response = await patchSupportTicketIntake(current.id, {
        expectedRevision: current.revision,
        ...payload,
      });
      const saved = response?.data;
      setIntake(saved);
      intakeRef.current = saved;
      return saved;
    },
    [location.state, navigate],
  );

  // The save loop lives in useIntakeDraftAutosave (commit 4): the edit-sequence,
  // queued-chain, dirty, and 'pending' guards were all load-bearing here, and this
  // page and the request page must not carry two copies of them. Every string a
  // reporter reads is still this page's own.
  const onConflict = useCallback(async () => {
    // Never discard the reporter's typed work on a conflict: refresh the server
    // revision and keep what is on screen so the next save wins.
    try {
      const refreshed = await getSupportTicketIntake(intakeRef.current.id);
      setIntake(refreshed?.data || intakeRef.current);
      intakeRef.current = refreshed?.data || intakeRef.current;
      return 'This ticket was changed in another tab. Your text is still here — save again to keep it.';
    } catch {
      return 'This ticket was changed elsewhere and PRIME could not reload it.';
    }
  }, []);

  const { saveState, saveMessage, setSaveMessage, markDirty, save, isDirty } =
    useIntakeDraftAutosave({
      buildPayload,
      persist,
      // Suspended off the describe step, exactly as the previous effect was.
      enabled: step === 'describe',
      watch: [fields, attachments],
      onConflict,
      describeError: (error) =>
        error?.message
          ? `PRIME could not save your draft: ${error.message}`
          : 'PRIME could not save your draft. Your text is still here — try again.',
    });

  function updateField(field, value) {
    markDirty();
    setFields((current) => ({ ...current, [field]: value }));
    // Editing a field retires its validation entry immediately: a summary that
    // keeps naming a field the reporter already fixed — and an input still
    // asserting aria-invalid — tells screen-reader and sighted users alike that
    // their fix did not take (visual-polish audit, flagged item 2). The next
    // Review/Send re-validates everything anyway.
    // The description control validates under its persisted key.
    const retired = field === 'description' ? [field, 'details.description'] : [field];
    setValidationKeys((current) =>
      current.some((key) => retired.includes(key))
        ? current.filter((key) => !retired.includes(key))
        : current,
    );
    // Touching a prefilled control makes it the reporter's own value; the label
    // must stop claiming a directory source.
    setProvenance((current) =>
      current[field] === 'directory_supplied' ? { ...current, [field]: 'user_entered' } : current,
    );
  }

  async function loadCaptures() {
    setCaptureLoadError(null);
    try {
      const response = await listFeedbackPackages({ limit: 25 });
      const packages = response?.data || [];
      const captures = [];
      for (const pkg of packages) {
        for (const screen of pkg.screens || []) {
          captures.push({
            captureId: screen.captureId || screen.screenId || screen.id,
            screenId: screen.screenId || screen.id || null,
            packageId: pkg.id,
            label: screen.route || screen.title || pkg.title || 'Screen capture',
          });
        }
      }
      setAvailableCaptures(captures);
    } catch (error) {
      setAvailableCaptures([]);
      setCaptureLoadError(
        error?.message || 'PRIME could not load your existing captures right now.',
      );
    }
  }

  function attachCapture(capture) {
    if (attachments.some((entry) => entry.captureId === capture.captureId)) return;
    if (attachments.length >= SUPPORT_TICKET_MAX_ATTACHMENTS) return;
    markDirty();
    setAttachments((current) => [...current, capture]);
  }

  function removeAttachment(captureId) {
    if (!attachments.some((entry) => entry.captureId === captureId)) return;
    markDirty();
    setAttachments((current) => current.filter((entry) => entry.captureId !== captureId));
  }

  async function goToReview() {
    setServerFindings([]);
    const missing = [
      ...structuralValidation.missing,
      ...structuralValidation.errors
        .filter(({ reason }) => reason !== 'required')
        .map(({ field }) => (field === 'description' ? 'details.description' : field)),
    ];
    if (!fields.title.trim()) missing.unshift('title');
    if (missing.length) {
      setValidationKeys(missing);
      // Move focus to the summary so a screen-reader user is taken to the list
      // rather than left where they clicked.
      setTimeout(() => validationSummaryRef.current?.focus(), 0);
      return;
    }
    setValidationKeys([]);
    setBusy(true);
    setPreviewError(null);
    let saved = null;
    try {
      saved = await save();
      if (!saved) return;
      if (isDirty()) {
        setSaveMessage(
          'You kept typing while PRIME was saving. Review again after the latest text is saved.',
        );
        return;
      }
      // The server returns no recipient, subject, body, copy, print, or download
      // artifact until the versioned protected-information statement is recorded.
      setFields((current) => ({ ...current, cuiAcknowledged: false }));
      setPreview({ authorizationRequired: true, intakeId: saved.id });
      setStep('review');
    } catch (error) {
      if (error?.details?.reason === 'sensitive_artifact_blocked') {
        const sensitiveFindings = error.details.sensitiveFindings || [];
        setServerFindings(sensitiveFindings);
        setPreview({
          blocked: true,
          intakeId: saved?.id || intakeRef.current?.id,
          // The server names the configured delivery mode on a blocked preview.
          // Without it a draft-only page would fall back to send wording the
          // moment the full preview payload is unavailable.
          delivery: error.details.deliveryMode ? { mode: error.details.deliveryMode } : null,
          readiness: { sensitiveBlocked: true, sensitiveFindings },
        });
        setStep('review');
      } else {
        setPreviewError(error?.message || 'PRIME could not prepare the email preview.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function confirmAndLoadPreview() {
    const current = intakeRef.current;
    if (!current || !fields.cuiAcknowledged) return;
    setBusy(true);
    setPreviewError(null);
    setServerFindings([]);
    try {
      const response = await acknowledgeSupportTicketCui(
        current.id,
        current.revision,
        SUPPORT_TICKET_ACKNOWLEDGEMENT_VERSION,
        { idempotencyKey: acknowledgementKeyRef.current },
      );
      const result = response?.data;
      setIntake(result?.intake || current);
      intakeRef.current = result?.intake || current;
      setPreview(result?.preview || null);
      setServerFindings(result?.preview?.readiness?.sensitiveFindings || []);
    } catch (error) {
      if (
        error?.details?.reason === 'sensitive_artifact_blocked' ||
        error?.details?.reason === 'intake_not_ready'
      ) {
        const sensitiveFindings = error.details.sensitiveFindings || [];
        setServerFindings(sensitiveFindings);
        setPreview({
          blocked: true,
          authorizationRequired: false,
          intakeId: current.id,
          delivery: error.details.deliveryMode ? { mode: error.details.deliveryMode } : null,
          readiness: { sensitiveBlocked: sensitiveFindings.length > 0, sensitiveFindings },
        });
        setValidationKeys(error.details.missing || []);
      } else {
        setPreviewError(
          error?.message || 'PRIME could not record the confirmation or prepare the preview.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendTicket() {
    setBusy(true);
    setValidationKeys([]);
    setServerFindings([]);
    try {
      const saved = intakeRef.current;
      if (!saved || !preview?.previewBinding) return;
      const prepared =
        saved.status === 'ready_for_review'
          ? { data: saved }
          : await prepareSupportTicketIntake(saved.id, saved.revision, preview?.previewBinding);
      const readyIntake = prepared?.data;
      setIntake(readyIntake);
      intakeRef.current = readyIntake;
      const submitted = await submitSupportTicketIntake(readyIntake.id, readyIntake.revision, {
        idempotencyKey: submitKeyRef.current,
        previewBinding: preview?.previewBinding,
      });
      setSubmission(submitted?.data || null);
      setIntake(submitted?.data?.intake || readyIntake);
      intakeRef.current = submitted?.data?.intake || readyIntake;
      // The draft-saved hint has served its purpose: on the confirmation step it
      // sits beside "PRIME recorded your ticket" and reads as a competing claim
      // (visual-polish audit, flagged item 3). RequestIntakePage does the same.
      setSaveMessage('');
      setStep('sent');
    } catch (error) {
      if (error?.details?.reason === 'support_preview_stale') {
        submitKeyRef.current = newIdempotencyKey();
        setPreview(null);
        setStep('describe');
        setPreviewError(
          'The ticket or its delivery settings changed. Review the latest ticket again before continuing.',
        );
      } else if (error?.details?.reason === 'intake_not_ready') {
        setValidationKeys(error.details.missing || []);
        setServerFindings(error.details.sensitiveFindings || []);
        setTimeout(() => validationSummaryRef.current?.focus(), 0);
      } else {
        // Truthful to the delivery mode: in draft_only nothing was ever going
        // to be emailed, so a failure is a failure to RECORD.
        const failureMode = preview?.delivery?.mode || null;
        setPreviewError(
          error?.message ||
            (failureMode === 'email'
              ? 'PRIME could not send this ticket. Nothing was emailed.'
              : 'PRIME could not record this ticket. Nothing was recorded and nothing was emailed.'),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function copyEmail() {
    if (
      step !== 'sent' ||
      !submission?.feedbackRecord ||
      !preview ||
      preview.blocked ||
      preview.readiness?.sensitiveBlocked
    )
      return;
    const text = `Subject: ${preview.subject}\n\n${preview.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setActionNote('The email text was copied to your clipboard.');
    } catch {
      setActionNote('PRIME could not use your clipboard. Select the text below and copy it.');
    }
  }

  function printEmail() {
    if (
      step !== 'sent' ||
      !submission?.feedbackRecord ||
      !preview ||
      preview.blocked ||
      preview.readiness?.sensitiveBlocked
    )
      return;
    if (typeof window.print !== 'function') {
      setActionNote('Printing is not available in this browser.');
      return;
    }
    window.print();
    setActionNote('The printable view was sent to your browser print dialog.');
  }

  const findings = useMemo(() => {
    // Server findings win: they are authoritative and already redacted. Client
    // findings only fill in for detectors the server has not run yet.
    if (serverFindings.length) return serverFindings;
    return clientFindings;
  }, [clientFindings, serverFindings]);

  const contextRows = useMemo(() => {
    if (preview?.contextSummary?.length) return preview.contextSummary;
    // Before a preview exists, show the reporter what the client PROPOSES, and
    // say plainly that the server finalizes the trusted fields.
    return Object.entries(proposedContext)
      .filter(([key]) => key !== 'schemaVersion')
      .map(([key, value]) => ({
        key,
        label: key,
        value: typeof value === 'object' ? JSON.stringify(value) : String(value),
      }));
  }, [preview, proposedContext]);

  if (loadState === 'loading') {
    return (
      <section className="support-ticket-page">
        <PageHeader eyebrow="Help" title="Report a problem" subtitle="Loading your draft…" />
        <p className="support-ticket-card-hint" role="status">
          Loading your saved draft…
        </p>
      </section>
    );
  }

  if (loadState === 'error') {
    return (
      <section className="support-ticket-page">
        <PageHeader eyebrow="Help" title="Report a problem" />
        <div
          className="support-ticket-alert tone-danger"
          role="alert"
          data-testid="support-ticket-load-error"
        >
          <h3>PRIME could not open this draft</h3>
          <p>{loadError?.message || 'The draft could not be loaded.'}</p>
          <p>
            <Link to="/feedback/support/new">Start a new ticket</Link> or{' '}
            <Link to="/feedback">go to My Feedback</Link>.
          </p>
        </div>
      </section>
    );
  }

  const deliveryMode = preview?.delivery?.mode || submission?.dispatch?.mode || null;
  const emailDelivery = deliveryMode === 'email';
  const recordOnly = Boolean(deliveryMode) && !emailDelivery;
  const steps = BASE_STEPS.map((entry) =>
    entry.id === 'review'
      ? {
          ...entry,
          label: recordOnly ? 'Review and record' : emailDelivery ? 'Review and send' : entry.label,
        }
      : entry,
  );
  const currentStepIndex = steps.findIndex((entry) => entry.id === step);

  return (
    <section className="support-ticket-page" data-testid="support-ticket-page">
      <PageHeader
        eyebrow="Help"
        title="Report a problem"
        subtitle="PRIME prepares a GSC ticket for the MHS Service Desk and keeps a copy in My Feedback."
      />

      <ol
        className="support-ticket-steps support-ticket-no-print"
        data-testid="support-ticket-steps"
      >
        {steps.map((entry, index) => (
          <li
            key={entry.id}
            aria-current={entry.id === step ? 'step' : undefined}
            data-complete={index < currentStepIndex ? 'true' : 'false'}
          >
            <span className="step-index" aria-hidden="true">
              {index < currentStepIndex ? '✓' : index + 1}
            </span>
            <span>{entry.label}</span>
          </li>
        ))}
      </ol>

      {/* One live region for save state, announced politely so it never
          interrupts typing. */}
      <p
        className={`support-ticket-savestate support-ticket-no-print${saveState === 'failed' ? ' is-failed' : ''}`}
        aria-live="polite"
        data-testid="support-ticket-save-state"
      >
        {saveMessage}
      </p>

      {validationKeys.length ? (
        <div
          className="support-ticket-alert tone-danger support-ticket-no-print"
          role="alert"
          tabIndex={-1}
          ref={validationSummaryRef}
          data-testid="support-ticket-validation-summary"
        >
          <h3>
            {validationKeys.length} thing{validationKeys.length === 1 ? '' : 's'} still need your
            attention
          </h3>
          <ul>
            {validationKeys.map((key) => (
              <li key={key}>
                <a
                  href={`#${validationControlId(key)}`}
                  onClick={(event) => {
                    event.preventDefault();
                    const target = document.getElementById(validationControlId(key));
                    target?.focus();
                    target?.scrollIntoView?.({ block: 'center' });
                  }}
                >
                  {validationLabel(key)}
                </a>
                {key === 'cuiAcknowledged'
                  ? ' — confirm the ticket has no protected information.'
                  : ' — this is required.'}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {previewError ? (
        <div
          className="support-ticket-alert tone-danger"
          role="alert"
          data-testid="support-ticket-error"
        >
          <h3>Something went wrong</h3>
          <p>{previewError}</p>
        </div>
      ) : null}

      {step === 'describe' ? (
        <>
          <div className="support-ticket-card">
            <h2>Who should the service desk contact?</h2>
            <p className="support-ticket-card-hint">
              PRIME filled in what it already knows about you. Correct anything that is wrong — the
              service desk uses these details to reach you.
            </p>
            <div className="support-ticket-grid">
              <div className="form-field">
                <label className="form-label" htmlFor={fieldId('title')}>
                  Short summary{' '}
                  <span className="form-required" aria-hidden>
                    {' '}
                    *
                  </span>
                </label>
                <div className="form-control">
                  <input
                    className="form-input"
                    id={fieldId('title')}
                    name="title"
                    type="text"
                    value={fields.title}
                    maxLength={400}
                    required
                    aria-describedby={`${fieldId('title')}-hint`}
                    onChange={(event) => updateField('title', event.target.value)}
                  />
                </div>
                <div className="form-hint" id={`${fieldId('title')}-hint`}>
                  One line PRIME uses to track the ticket, for example &ldquo;Financials export
                  fails&rdquo;.
                </div>
              </div>

              {TEXT_FIELDS.map((field) => (
                <div className="form-field" key={field}>
                  <label className="form-label" htmlFor={fieldId(field)}>
                    {SUPPORT_TICKET_FIELD_LABELS[field]}
                    {SUPPORT_TICKET_REQUIRED_FIELDS.includes(field) ? (
                      <span className="form-required" aria-hidden>
                        {' '}
                        *
                      </span>
                    ) : null}
                  </label>
                  <div className="form-control">
                    <input
                      className="form-input"
                      id={fieldId(field)}
                      name={field}
                      type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                      autoComplete={
                        {
                          name: 'name',
                          email: 'email',
                          phone: 'tel',
                          organization: 'organization',
                        }[field] || 'off'
                      }
                      value={fields[field]}
                      maxLength={SUPPORT_TICKET_FIELD_LIMITS[field]}
                      required={SUPPORT_TICKET_REQUIRED_FIELDS.includes(field)}
                      aria-invalid={validationKeys.includes(field) ? 'true' : undefined}
                      aria-describedby={`${fieldId(field)}-hint`}
                      onChange={(event) => updateField(field, event.target.value)}
                    />
                  </div>
                  <div className="form-hint" id={`${fieldId(field)}-hint`}>
                    {field === 'fullComputerName' ? (
                      <>
                        Your browser cannot detect this. Copy it from your workstation if you know
                        it — PRIME will read it from a trusted device record in a future
                        release.{' '}
                      </>
                    ) : null}
                    {provenance[field] ? (
                      <span
                        className="support-ticket-provenance"
                        data-testid={`provenance-${field}`}
                      >
                        {SUPPORT_TICKET_FIELD_PROVENANCE_LABELS[provenance[field]]}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}

              <div className="form-field">
                <label className="form-label" htmlFor={fieldId('category')}>
                  {SUPPORT_TICKET_FIELD_LABELS.category}
                  <span className="form-required" aria-hidden>
                    {' '}
                    *
                  </span>
                </label>
                <div className="form-control">
                  <div className="form-select-wrap">
                    <select
                      className="form-input form-select"
                      id={fieldId('category')}
                      name="category"
                      value={fields.category}
                      required
                      aria-invalid={validationKeys.includes('category') ? 'true' : undefined}
                      aria-describedby={`${fieldId('category')}-hint`}
                      onChange={(event) => updateField('category', event.target.value)}
                    >
                      <option value="">Choose a category…</option>
                      {SUPPORT_TICKET_CATEGORIES.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                    <span className="form-select-chevron" aria-hidden>
                      ▾
                    </span>
                  </div>
                </div>
                <div className="form-hint" id={`${fieldId('category')}-hint`}>
                  The service desk routes the ticket from this choice, and it becomes the email
                  subject.
                </div>
              </div>
            </div>
          </div>

          {/* The CUI warning sits immediately BEFORE the description, where the
              protected information would actually be typed. */}
          <CuiWarning id="support-ticket-cui-describe" />

          <div className="support-ticket-card">
            <h2>What went wrong?</h2>
            <div className="form-field">
              <label className="form-label" htmlFor={fieldId('description')}>
                {SUPPORT_TICKET_FIELD_LABELS.description}
                <span className="form-required" aria-hidden>
                  {' '}
                  *
                </span>
              </label>
              <div className="form-control">
                <textarea
                  className="form-input form-textarea"
                  id={fieldId('description')}
                  name="description"
                  rows={8}
                  value={fields.description}
                  maxLength={SUPPORT_TICKET_FIELD_LIMITS.description}
                  required
                  aria-invalid={validationKeys.includes('details.description') ? 'true' : undefined}
                  aria-describedby={`${fieldId('description')}-hint`}
                  onChange={(event) => updateField('description', event.target.value)}
                />
              </div>
              <div className="form-hint" id={`${fieldId('description')}-hint`}>
                What you were doing, what you expected, and what happened instead.{' '}
                <span
                  className={`support-ticket-char-count${
                    fields.description.length > SUPPORT_TICKET_FIELD_LIMITS.description
                      ? ' is-over'
                      : ''
                  }`}
                >
                  {fields.description.length} of {SUPPORT_TICKET_FIELD_LIMITS.description}{' '}
                  characters
                </span>
              </div>
            </div>

            <FindingList findings={findings} testId="support-ticket-findings" />

            <label className="support-ticket-check">
              <input
                type="checkbox"
                id={fieldId('protectedInformationRequired')}
                name="protectedInformationRequired"
                checked={fields.protectedInformationRequired}
                onChange={(event) =>
                  updateField('protectedInformationRequired', event.target.checked)
                }
              />
              <span>{SUPPORT_TICKET_PROTECTED_INFO_INDICATOR}</span>
            </label>
          </div>

          <div className="support-ticket-card support-ticket-attachments">
            <h2>Attachments</h2>
            <p className="support-ticket-card-hint">
              PRIME attaches nothing automatically — no screenshot, no page content, no form values.
              You can attach a screen capture you already made, and only the ones you pick are
              referenced.
            </p>
            {attachments.length ? (
              <ul
                className="support-ticket-attachment-list"
                data-testid="support-ticket-attachments"
              >
                {attachments.map((attachment) => (
                  <li key={attachment.captureId}>
                    <span>{attachment.label || attachment.captureId}</span>
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => removeAttachment(attachment.captureId)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {availableCaptures === null ? (
              <button
                type="button"
                className="btn ghost sm"
                data-testid="support-ticket-load-captures"
                onClick={() => void loadCaptures()}
              >
                Attach an existing screen capture
              </button>
            ) : (
              <>
                {captureLoadError ? (
                  <p className="support-ticket-card-hint" role="status">
                    {captureLoadError}
                  </p>
                ) : null}
                {availableCaptures.length ? (
                  <ul className="support-ticket-attachment-list">
                    {availableCaptures.map((capture) => (
                      <li key={capture.captureId}>
                        <span>{capture.label}</span>
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={
                            attachments.length >= SUPPORT_TICKET_MAX_ATTACHMENTS ||
                            attachments.some((entry) => entry.captureId === capture.captureId)
                          }
                          onClick={() => attachCapture(capture)}
                        >
                          Attach
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="support-ticket-card-hint">
                    You have no saved screen captures to attach.
                  </p>
                )}
              </>
            )}
          </div>

          <details
            className="support-ticket-card support-ticket-context"
            data-testid="support-ticket-context"
          >
            <summary>Technical context included with this ticket</summary>
            <p className="support-ticket-card-hint">
              PRIME attaches this to help the service desk reproduce the problem. It contains no
              cookies, no passwords, no page content, and no record data. PRIME finalizes your
              roles, sign-in mode, and build details on the server, so those may differ from what is
              listed here.
            </p>
            <dl className="support-ticket-context-rows">
              {contextRows.map((row) => (
                <div key={row.key}>
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </details>

          <div className="support-ticket-actions support-ticket-no-print">
            <button
              type="button"
              className="btn primary"
              data-testid="support-ticket-review"
              disabled={busy}
              onClick={() => void goToReview()}
            >
              {busy ? 'Preparing…' : 'Review the email'}
            </button>
            <button
              type="button"
              className="btn ghost"
              data-testid="support-ticket-save"
              disabled={busy || saveState === 'saving'}
              onClick={() => void save()}
            >
              Save draft
            </button>
            <Link className="btn ghost" to="/feedback">
              My Feedback
            </Link>
          </div>
        </>
      ) : null}

      {step === 'review' && preview ? (
        <>
          {/* Second, mandatory placement: the reporter sees the warning again
              with the exact text that is about to leave PRIME in front of them. */}
          <CuiWarning id="support-ticket-cui-review" deliveryMode={deliveryMode} />

          <FindingList
            findings={findings}
            testId="support-ticket-review-findings"
            forceBlocking={preview.blocked || preview.readiness?.sensitiveBlocked}
            recordOnly={recordOnly}
          />

          <div className="support-ticket-card">
            {preview.authorizationRequired && !preview.subject ? (
              <>
                <h2>Confirm before PRIME generates the email</h2>
                <p data-testid="support-ticket-preview-locked">
                  PRIME has not returned recipients, a subject, an email body, or a downloadable
                  file. Confirm the protected-information statement below to generate the current
                  ticket preview.
                </p>
              </>
            ) : preview.blocked || preview.readiness?.sensitiveBlocked ? (
              <>
                <h2>Email preview unavailable</h2>
                <p data-testid="support-ticket-preview-blocked">
                  Remove the protected information listed above before previewing, copying,
                  printing, or downloading this ticket.
                </p>
                <div className="support-ticket-actions support-ticket-no-print">
                  <button
                    type="button"
                    className="btn ghost sm"
                    data-testid="support-ticket-copy"
                    disabled
                  >
                    Copy the email text
                  </button>
                  <button
                    type="button"
                    className="btn ghost sm"
                    data-testid="support-ticket-print"
                    disabled
                  >
                    Print
                  </button>
                  <button
                    type="button"
                    className="btn ghost sm"
                    data-testid="support-ticket-download-eml"
                    disabled
                  >
                    Download .eml
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>
                  {recordOnly
                    ? 'Review the email draft PRIME will record'
                    : 'This is exactly what the service desk receives'}
                </h2>
                <dl className="support-ticket-preview-meta">
                  <div>
                    <dt>To</dt>
                    <dd data-testid="support-ticket-preview-to">
                      {preview.recipients.recipientPosture === 'user_addressed_draft'
                        ? 'Add recipients in your approved mail client.'
                        : preview.recipients.to.join(', ')}
                    </dd>
                  </div>
                  {preview.recipients.cc?.length ? (
                    <div>
                      <dt>Cc</dt>
                      <dd>{preview.recipients.cc.join(', ')}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Subject</dt>
                    <dd data-testid="support-ticket-preview-subject">{preview.subject}</dd>
                  </div>
                </dl>
                <p
                  className="support-ticket-card-hint"
                  data-testid="support-ticket-recipient-posture"
                >
                  {preview.recipients.recipientPosture === 'user_addressed_draft'
                    ? 'User-addressed draft — PRIME leaves To and Cc blank.'
                    : preview.recipients.recipientPosture === 'safe_sink'
                      ? 'Non-production safe recipient — no live service-desk mailbox is addressed.'
                      : 'Live recipient configuration — PRIME is addressing the configured service-desk mailbox.'}
                </p>
                <p className="support-ticket-card-hint">
                  {preview.recipients.recipientPosture === 'user_addressed_draft'
                    ? 'PRIME did not select recipients, send this message, or verify delivery.'
                    : recordOnly
                      ? 'PRIME sets the recipients shown in the draft. Recording the ticket does not email them.'
                      : 'PRIME sets the recipients. You cannot change them here, and this ticket is not sent to anyone else.'}
                </p>
                <div className="support-ticket-preview-body">
                  <pre data-testid="support-ticket-preview-body">{preview.body}</pre>
                </div>
                <div className="support-ticket-actions support-ticket-no-print">
                  <button
                    type="button"
                    className="btn ghost sm"
                    data-testid="support-ticket-copy"
                    disabled
                  >
                    Copy email text
                  </button>
                  <button
                    type="button"
                    className="btn ghost sm"
                    data-testid="support-ticket-print"
                    disabled
                  >
                    Print
                  </button>
                  <button
                    type="button"
                    className="btn ghost sm"
                    data-testid="support-ticket-download-eml"
                    disabled
                  >
                    Download email draft (.eml)
                  </button>
                  <span
                    className="support-ticket-action-note"
                    aria-live="polite"
                    data-testid="support-ticket-action-note"
                  >
                    {actionNote}
                  </span>
                </div>
              </>
            )}
          </div>

          {preview.contextSummary?.length ? (
            <details className="support-ticket-card support-ticket-context">
              <summary>Technical context included with this ticket</summary>
              <dl className="support-ticket-context-rows">
                {preview.contextSummary.map((row) => (
                  <div key={row.key}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </details>
          ) : null}

          <div className="support-ticket-card support-ticket-no-print">
            <label className="support-ticket-check is-required">
              <input
                type="checkbox"
                id={fieldId('cuiAcknowledged')}
                name="cuiAcknowledged"
                checked={fields.cuiAcknowledged}
                aria-invalid={validationKeys.includes('cuiAcknowledged') ? 'true' : undefined}
                disabled={Boolean(preview.subject)}
                onChange={(event) =>
                  setFields((current) => ({
                    ...current,
                    cuiAcknowledged: event.target.checked,
                  }))
                }
              />
              <span>{SUPPORT_TICKET_ACKNOWLEDGEMENT}</span>
            </label>
            <div className="support-ticket-actions">
              <button
                type="button"
                className="btn primary"
                data-testid="support-ticket-send"
                // Two independent client gates, neither of which is the real one:
                // the server re-validates the acknowledgement AND re-runs the
                // sensitive scan inside the submit transaction. These only spare
                // the reporter a round trip that was always going to be refused.
                disabled={
                  busy ||
                  (!fields.cuiAcknowledged && !preview.previewBinding) ||
                  preview.readiness?.sensitiveBlocked
                }
                onClick={() =>
                  void (preview.authorizationRequired && !preview.subject
                    ? confirmAndLoadPreview()
                    : sendTicket())
                }
              >
                {busy
                  ? recordOnly
                    ? 'Recording…'
                    : 'Sending…'
                  : preview.authorizationRequired && !preview.subject
                    ? 'Confirm and review email'
                    : recordOnly
                      ? 'Record in PRIME'
                      : 'Send to the service desk'}
              </button>
              <button
                type="button"
                className="btn ghost"
                data-testid="support-ticket-back"
                onClick={() => setStep('describe')}
              >
                Back to the details
              </button>
              {preview.readiness?.sensitiveBlocked ? (
                <span className="support-ticket-action-note">
                  Go back and remove the protected information listed above before continuing.
                </span>
              ) : null}
              {!fields.cuiAcknowledged &&
              !preview.previewBinding &&
              !preview.readiness?.sensitiveBlocked ? (
                <span className="support-ticket-action-note">
                  Confirm the statement above to continue.
                </span>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      {step === 'sent' ? (
        <div
          className="support-ticket-alert tone-success"
          role="status"
          data-testid="support-ticket-confirmation"
        >
          <h3>PRIME recorded your ticket</h3>
          <p>
            Your ticket is saved in PRIME and the product team can see it in the feedback queue.
          </p>
          {/* The delivery language is the SERVER's, rendered verbatim. Composing
              it here would create a second source of truth for the one claim this
              whole vertical must never get wrong — whether the ticket left PRIME.
              The only string this component owns is the disabled-delivery case,
              where there is no dispatch to describe. */}
          <DeliveryState
            delivery={
              submission?.dispatch || {
                statusLabel: 'No email was prepared',
                statusDetail:
                  'Email delivery is turned off in this environment. PRIME recorded the ticket for the product team.',
                deliveredExternally: false,
                recipients: null,
              }
            }
            testId="support-ticket-delivery-state"
          />
          {submission?.dispatch?.mode === 'draft_only' && intake?.id ? (
            <div className="support-ticket-actions support-ticket-no-print">
              <a
                className="btn primary"
                data-testid="support-ticket-confirmation-download-eml"
                href={gscTicketEmlUrl(intake.id)}
                download={`${intake.id}-gsc-ticket.eml`}
              >
                Download email draft (.eml)
              </a>
              <button
                type="button"
                className="btn ghost"
                data-testid="support-ticket-confirmation-copy"
                onClick={() => void copyEmail()}
              >
                Copy email text
              </button>
              <button
                type="button"
                className="btn ghost"
                data-testid="support-ticket-confirmation-print"
                onClick={printEmail}
              >
                Print email text
              </button>
              <span
                className="support-ticket-action-note"
                aria-live="polite"
                data-testid="support-ticket-action-note"
              >
                {actionNote || 'Add recipients in your approved mail client.'}
              </span>
            </div>
          ) : null}
          <p>
            <Link to="/feedback">Track it in My Feedback</Link>
            {intake?.id ? (
              <>
                {' · '}
                <Link to={`/feedback/support/${intake.id}`}>Open this ticket</Link>
              </>
            ) : null}
          </p>
        </div>
      ) : null}

      {/* Role list is read-only context for the reporter; rendered last so it
          never competes with the form for attention. */}
      <p className="support-ticket-card-hint support-ticket-no-print">
        Signed in as {user?.displayName || 'a PRIME user'}
        {roles?.length ? ` (${roles.join(', ')})` : ''}. PRIME attaches this identity to the ticket
        for you.
      </p>
    </section>
  );
}

export default SupportTicketPage;
